import { useState, useEffect } from 'react';
import { Activity, Calendar, Users, DollarSign, FileText, WifiOff } from 'lucide-react';
import './InitialLoadScreen.css';

const features = [
    {
        icon: Calendar,
        title: "Appointment Tracking",
        description: "Track all your patient appointments in one place with our intuitive calendar view"
    },
    {
        icon: Users,
        title: "Patient Management",
        description: "Manage your patient records securely with easy access to medical history"
    },
    {
        icon: DollarSign,
        title: "Facility Charge Tracking",
        description: "Automatic facility_charge calculations for referrals - never miss a payment"
    },
    {
        icon: FileText,
        title: "Invoice Generation",
        description: "Generate professional invoices with just one click"
    },
    {
        icon: WifiOff,
        title: "Offline Support",
        description: "Works even without internet - your data syncs when you're back online"
    }
];

export function InitialLoadScreen() {
    const [currentFeature, setCurrentFeature] = useState(0);
    const [progress, setProgress] = useState(0);

    // Rotate through features every 1.5 seconds (faster so users see the carousel)
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentFeature(prev => (prev + 1) % features.length);
        }, 1500);
        return () => clearInterval(interval);
    }, []);

    // Animate progress bar
    useEffect(() => {
        const interval = setInterval(() => {
            setProgress(prev => {
                if (prev >= 90) return prev; // Cap at 90% until actually complete
                return prev + Math.random() * 5;
            });
        }, 300);
        return () => clearInterval(interval);
    }, []);

    const CurrentIcon = features[currentFeature].icon;

    return (
        <div className="initial-load-screen">
            <div className="load-screen-content">
                {/* Logo and App Name */}
                <div className="load-screen-header">
                    <div className="load-logo-container">
                        <Activity size={48} className="load-logo-icon" />
                    </div>
                    <h1 className="load-app-name">ClinicNode</h1>
                    <p className="load-tagline">Setting up your workspace...</p>
                </div>

                {/* Progress Bar */}
                <div className="load-progress-container">
                    <div className="load-progress-bar">
                        <div
                            className="load-progress-fill"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <span className="load-progress-text">Syncing your data...</span>
                </div>

                {/* Feature Carousel */}
                <div className="load-feature-card">
                    <div className="load-feature-icon-container">
                        <CurrentIcon size={32} className="load-feature-icon" />
                    </div>
                    <h2 className="load-feature-title">{features[currentFeature].title}</h2>
                    <p className="load-feature-description">{features[currentFeature].description}</p>

                    {/* Carousel Indicators */}
                    <div className="load-carousel-indicators">
                        {features.map((_, index) => (
                            <button
                                key={index}
                                className={`load-indicator ${index === currentFeature ? 'active' : ''}`}
                                onClick={() => setCurrentFeature(index)}
                                aria-label={`Feature ${index + 1}`}
                            />
                        ))}
                    </div>
                </div>

                {/* Tips */}
                <p className="load-tip">
                    💡 Tip: You can use ClinicNode even when offline!
                </p>
            </div>
        </div>
    );
}
